import { fetchFromApi, fetchFromApiPlain, postToApi } from '@/utils/fetch/resolveFetch';
import type { ApiResponse, Filters, PaymentType, RoomWithPeople, Verification } from '@/interfaces';
import { roomsToQuery } from '@/utils/url/queryParamConverters';
import type {
    VisBookOrderGroup,
    VisBookReservation,
    VisBookTermsResponse,
    VisBookVerificationResponse,
    VisBookWebproduct,
} from '@/server/integration/visbook/visbookResponseTypes';
import { formatISODate } from 'shared/util/datetime-fns';
import type { BookingCustomer } from '@/interfaces/bookingPage';
import { filtersToBookingPageBrowserQueryParams } from '@/utils/url/bookingPageQueryParams';

export const visBookGetProduct = async (
    hotelId: number,
    productId: number,
    priceId: number,
    checkInDate: Date | number,
    checkOutDate: Date | number,
    rooms: readonly RoomWithPeople[],
): Promise<ApiResponse<VisBookWebproduct>> => {
    const url = `/api/visbook/${hotelId}/products/${productId}/?${[
        `checkInDate=${formatISODate(checkInDate)}`,
        `checkOutDate=${formatISODate(checkOutDate)}`,
        `${roomsToQuery(rooms).join('&')}`,
        `priceId=${priceId}`,
    ].join('&')}`;
    return fetchFromApi(url);
};

export const visBookMakeReservation = async (
    hotelId: number,
    productId: number,
    priceId: number,
    checkInDate: Date | number,
    checkOutDate: Date | number,
    rooms: readonly RoomWithPeople[],
): Promise<ApiResponse<VisBookReservation>> =>
    fetchFromApi(
        `/api/visbook/${hotelId}/reserve/${productId}/?checkInDate=${formatISODate(checkInDate)}&checkOutDate=${formatISODate(
            checkOutDate,
        )}&${roomsToQuery(rooms).join('&')}&priceId=${priceId}`,
    );

export const visBookInitiatePayment = async (
    filters: Filters,
    hotelId: number,
    reservation: VisBookReservation,
    customer: BookingCustomer,
    paymentType: PaymentType,
    totalPrice: number,
    verification: Verification,
    authentication: string,
    bookingParams: string,
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
): Promise<ApiResponse<any>> => {
    const visBookCustomer = {
        email: customer.email,
        firstName: customer.firstName,
        lastName: customer.lastName,
        company: customer.company,
        city: customer.area,
        country: customer.country,
        address: customer.address,
        zipCode: customer.zipCode,
        phone: undefined,
    };
    if (verification.phone === 'verified') {
        visBookCustomer.phone = customer.phone;
    } else {
        visBookCustomer.phone = `+${customer.phoneCountryCode} ${customer.phone}`;
    }
    const url = `/api/visbook/${hotelId}/payment/?${filtersToBookingPageBrowserQueryParams(filters, bookingParams)}`;
    return postToApi(
        url,
        JSON.stringify({
            reservationId: reservation.reservationId,
            encryptedCompanyId: reservation.encryptedCompanyId,
            authentication,
            paymentType,
            amount: totalPrice,
            customer: visBookCustomer,
            termsAccepted: customer.termsAccepted,
        }),
        {
            'Content-Type': 'application/json',
        },
    );
};

export const visBookGetReceipt = async (
    hotelId: number,
    orderGroupId: number,
    authentication: string,
): Promise<ApiResponse<VisBookOrderGroup>> =>
    postToApi(`/api/visbook/${hotelId}/receipt/${orderGroupId}/`, JSON.stringify({ authentication }));

// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const visBookLoginByEmail = async (hotelId: number, email: string): Promise<ApiResponse<any>> =>
    fetchFromApiPlain(`/api/visbook/${hotelId}/register/?email=${email}`);

// biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
export const visBookLoginBySms = async (hotelId: number, phoneCountryCode: string, phone: string): Promise<ApiResponse<any>> =>
    fetchFromApiPlain(`/api/visbook/${hotelId}/register/?cc=${phoneCountryCode}&phone=${phone}`);

export const visBookVerifyToken = async (
    hotelId: number,
    type: 'email' | 'phone',
    token: string,
): Promise<ApiResponse<VisBookVerificationResponse>> => fetchFromApi(`/api/visbook/${hotelId}/verify/${type}/?token=${token}`);

export const visBookPingReservation = async (
    hotelId: number,
    reservationId: number,
    encryptedCompanyId: string,
    // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
): Promise<ApiResponse<any>> =>
    fetchFromApiPlain(`/api/visbook/${hotelId}/ping/?reservationId=${reservationId}&encryptedCompanyId=${encryptedCompanyId}`);

export const visBookGetTerms = async (
    hotelId: number,
    reservationId: number,
    encryptedCompanyId: string,
): Promise<ApiResponse<VisBookTermsResponse>> =>
    fetchFromApi(`/api/visbook/${hotelId}/terms/?reservationId=${reservationId}&encryptedCompanyId=${encryptedCompanyId}`);
